<template>
  <div :class="classes">
    <template v-if="loading">
      <template>
        <Skeleton class="h-[64px] mb-5" />

        <Skeleton class="h-[48px] mb-5" />
      </template>

      <div
        v-for="n in 3"
        :key="n"
        class="w-full h-[64px] flex items-center first:border-t border-b border-gray-800/8"
      >
        <Skeleton class="w-6 h-6 rounded-full mr-2.5" />
        <div>
          <Skeleton class="w-[200px] h-[14px] mb-1.5" />
          <Skeleton class="w-[140px] h-[12px]" />
        </div>
      </div>
    </template>

    <div v-else class="flex flex-col gap-y-[14px]">
      <UpgradeToProCallout v-if="showProCallout">
        Invite options are only available with a Pro Membership
      </UpgradeToProCallout>

      <div :class="inviteClasses" class="flex flex-col gap-y-[14px]">
        <FileInfo :file="props.file">
          Invite viewers who are not owners or authors
        </FileInfo>

        <InviteInput
          v-model="usersToInvite"
          :disabled="!canInvite"
          :show-own-entity="true"
        />

        <Button
          v-if="!hideSendButton"
          variant="primary"
          class="ml-auto"
          :disabled="!canSend"
          :loading="sendInvitesLoading"
          @click="handleInviteClick"
        >
          Send invite
        </Button>
      </div>

      <Table class="border-t-0" :class="!items.length && inviteClasses">
        <Table.Head>
          <Table.Header primary>Name</Table.Header>
          <Table.Header align="right" class="w-[120px] flex!">
            Can download
          </Table.Header>
          <Table.Header class="w-[44px] flex!" />
        </Table.Head>

        <template v-if="items.length">
          <Table.Row
            v-for="item in items"
            :key="getKey(item)"
            :hover="false"
            class="cursor-default!"
          >
            <template #media>
              <Avatar
                v-if="isEntity(item)"
                :size="24"
                :src="getEntityAvatar(item)"
              />
              <div
                v-else
                class="size-6 flex items-center justify-center opacity-50"
              >
                <SendIcon class="text-gray-800" />
              </div>
            </template>

            <Table.Cell primary class="min-h-[64px]!">
              <div v-truncate class="w-full">
                <div data-truncate-target :class="getNameClass(item)">
                  {{ getEntityNameOrEmail(item) }}
                </div>
                <div class="text-[13px] leading-[18px] text-gray-800/50">
                  <template v-if="isUser(item)">
                    {{ (getEntityData(item) as UserType).username }}
                  </template>
                  <template v-if="isOrganization(item)">
                    Managed by {{ getOrgOwnerName(item) }}
                  </template>
                </div>
              </div>
            </Table.Cell>

            <Table.Cell align="right" class="w-[120px] min-h-[64px]!">
              <Toggle
                :size="isDesktopLayout ? 'md' : 'lg'"
                :model-value="canDownload(item)"
                @update:modelValue="(value) => handleToggleUpdate(item, value)"
              />
            </Table.Cell>

            <Table.Cell align="right" class="w-[44px] min-h-[64px]! pr-[7px]">
              <RowActionsMenu
                v-if="showThreeDots(item)"
                @remove="handleRemoveClick(item)"
              />
            </Table.Cell>
          </Table.Row>
        </template>

        <p
          v-else
          class="py-10 text-center text-[14px] leading-[20px] text-gray-600"
        >
          No invites sent
        </p>
      </Table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { openModal, Modals } from '@/shared/dialogs/modals'
import { graphql } from '@/shared/graphql/router'
import {
  FileRole,
  FilePermission,
  InviteType,
  type File,
  type User,
  type Organization,
  type ViewersConnection,
} from '@/shared/graphql/router/graphql'
import {
  UserPermission,
  type Entity as LegacyEntity,
  type BasicUser as LegacyBasicUser,
  type Organization as LegacyOrganization,
} from '@/shared/graphql/legacy/generated'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { Avatar, Button, Skeleton, Toggle, Table } from '@dacr/library'
import { SendIcon } from '@dacr/icons'
import RowActionsMenu from '../RowActionsMenu.vue'
import { computed, ref } from 'vue'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useUserStore } from '@/shared/stores/user'

import FileInfo from '@/features/explorer/components/FileInfo.vue'
import InviteInput from '@/features/search/components/InviteInput.vue'
import UpgradeToProCallout from '@/features/billing/components/UpgradeToProCallout.vue'
import { useDevice } from '@/shared/composables/useDevice'

type UserType = User
type OrgType = Organization

const FILE_VIEWERS_QUERY = graphql(`
  query FileViewers($id: ID!) {
    file(id: $id) {
      id
      viewers(input: { first: 100 }) {
        totalCount
        edges {
          node {
            ... on ViewerEntity {
              role
              permissions
              entity {
                ... on User {
                  id
                  name
                  username
                  avatar {
                    url
                  }
                }
                ... on Organization {
                  id
                  name
                  avatar {
                    url
                  }
                  owner {
                    ... on User {
                      id
                      name
                    }
                  }
                }
              }
            }
            ... on EmailInvite {
              email
            }
          }
        }
      }
    }
  }
`)

const ADD_VIEWERS_MUTATION = graphql(`
  mutation AddViewers($input: AddViewersInput!) {
    addViewers(input: $input) {
      error {
        code
        message
      }
      file {
        id
        viewers(input: { first: 100 }) {
          totalCount
          edges {
            node {
              ... on ViewerEntity {
                role
                permissions
                entity {
                  ... on User {
                    id
                    name
                    username
                    avatar {
                      url
                    }
                  }
                  ... on Organization {
                    id
                    name
                    avatar {
                      url
                    }
                    owner {
                      ... on User {
                        id
                        name
                      }
                    }
                  }
                }
              }
              ... on EmailInvite {
                email
              }
            }
          }
        }
      }
    }
  }
`)

const UPDATE_VIEWER_PERMISSIONS_MUTATION = graphql(`
  mutation UpdateViewerPermissions($input: UpdateViewerPermissionsInput!) {
    updateViewerPermissions(input: $input) {
      file {
        id
        viewers(input: { first: 100 }) {
          totalCount
          edges {
            node {
              ... on ViewerEntity {
                role
                permissions
                entity {
                  ... on User {
                    id
                    name
                    username
                    avatar {
                      url
                    }
                  }
                  ... on Organization {
                    id
                    name
                    avatar {
                      url
                    }
                    owner {
                      ... on User {
                        id
                        name
                      }
                    }
                  }
                }
              }
              ... on EmailInvite {
                email
              }
            }
          }
        }
      }
    }
  }
`)

const props = defineProps<{
  file: File
  hideSendButton?: boolean
}>()

const { result, loading } = useQuery(
  FILE_VIEWERS_QUERY,
  { id: props.file.copyright.id || '' },
  {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    clientId: 'router',
  }
)

const { mutate: updatePermissions } = useMutation(
  UPDATE_VIEWER_PERMISSIONS_MUTATION,
  { clientId: 'router' }
)

const { mutate: sendInvites, loading: sendInvitesLoading } = useMutation(
  ADD_VIEWERS_MUTATION,
  { clientId: 'router' }
)

const toastStore = useToastStore()
const userStore = useUserStore()
const { isDesktopLayout } = useDevice()

const usersToInvite = ref<LegacyEntity[]>([])

type ViewerNode = NonNullable<
  NonNullable<typeof result.value>['file']
>['viewers']['edges'][number]['node']

const classes = computed(() => ({
  'overflow-y-scroll': (items.value?.length || 0) > 4,
  'overflow-y-visible': (items.value?.length || 0) < 4,
}))

const isBasicUser = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const isOwner = computed(() => props.file.role === FileRole.Owner)

const canInvite = computed(() => !isOwner.value || !isBasicUser.value)

const canSend = computed(
  () => usersToInvite.value.length > 0 && canInvite.value
)

const showProCallout = computed(() => isBasicUser.value && isOwner.value)

const items = computed(
  () => result.value?.file?.viewers.edges.map((e) => e.node) ?? []
)

const inviteClasses = computed(() => ({
  'opacity-30 pointer-events-none': isOwner.value && isBasicUser.value,
}))

type ViewerEmailInvite = Extract<ViewerNode, { email: string }>
type ViewerEntityNode = Exclude<ViewerNode, ViewerEmailInvite>

const isEmailInvite = (item: ViewerNode): item is ViewerEmailInvite =>
  'email' in item

const asEntity = (item: ViewerNode) => item as ViewerEntityNode

const getNameClass = (item: ViewerNode) => ({
  'text-[13px] opacity-50': isEmailInvite(item),
})

const getKey = (item: ViewerNode) =>
  isEmailInvite(item) ? item.email : asEntity(item).entity.id

const isEntity = (item: ViewerNode) => !isEmailInvite(item)

const getEntityData = (item: ViewerNode) =>
  isEmailInvite(item) ? null : asEntity(item).entity

const getEntityAvatar = (item: ViewerNode) =>
  isEmailInvite(item) ? undefined : asEntity(item).entity.avatar.url

const getEntityNameOrEmail = (item: ViewerNode) =>
  isEmailInvite(item) ? item.email : asEntity(item).entity.name

const getInviteType = (user: LegacyEntity) => {
  if (user.id === user.name) return InviteType.Email
  return user.__typename === 'BasicUser' ? InviteType.User : InviteType.Org
}

const showThreeDots = (item: ViewerNode) => {
  if (isEmailInvite(item)) return isOwner.value
  const e = asEntity(item)
  if (isOwner.value) return true
  if (e.entity.__typename === 'Organization') {
    const orgOwner = (e.entity as OrgType).owner
    return orgOwner?.__typename === 'User' && orgOwner.id === userStore.user?.id
  }
  return e.entity.id === userStore.user?.id
}

const isUser = (item: ViewerNode) =>
  !isEmailInvite(item) && asEntity(item).entity.__typename === 'User'

const isOrganization = (item: ViewerNode) =>
  !isEmailInvite(item) && asEntity(item).entity.__typename === 'Organization'

const getOrgOwnerName = (item: ViewerNode) => {
  if (isEmailInvite(item)) return ''
  const org = asEntity(item).entity as OrgType
  return org.owner?.__typename === 'User' ? org.owner.name : ''
}

const downloadOverrides = ref<Record<string, boolean>>({})

const canDownload = (item: ViewerNode) => {
  if (isEmailInvite(item)) return false

  const e = asEntity(item)
  const override = downloadOverrides.value[e.entity.id]

  if (override !== undefined) return override

  return e.permissions.includes(FilePermission.Download)
}

const handleToggleUpdate = (item: ViewerNode, value: boolean) => {
  if (isEmailInvite(item)) return

  const entityId = asEntity(item).entity.id
  const previous = downloadOverrides.value[entityId]

  downloadOverrides.value = { ...downloadOverrides.value, [entityId]: value }

  updatePermissions({
    input: {
      fileId: props.file.id,
      updates: [{ id: entityId, canDownload: value }],
    },
  }).catch((error) => {
    console.error(error)

    if (downloadOverrides.value[entityId] !== value) return

    const reverted = { ...downloadOverrides.value }

    if (previous === undefined) {
      delete reverted[entityId]
    } else {
      reverted[entityId] = previous
    }

    downloadOverrides.value = reverted

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Could not update download access - please try again later',
    })
  })
}

const handleRemoveClick = (item: ViewerNode) => {
  let inviteType: InviteType
  let invitePayload: { id?: string; email?: string }

  if (isEmailInvite(item)) {
    inviteType = InviteType.Email
    invitePayload = { email: item.email }
  } else {
    const e = asEntity(item)
    inviteType =
      e.entity.__typename === 'User' ? InviteType.User : InviteType.Org
    invitePayload = { id: e.entity.id }
  }

  openModal(Modals.RemoveFileAccess, {
    stack: true,
    props: {
      fileId: props.file.id,
      invite: {
        ...invitePayload,
        type: inviteType,
      },
      name: getEntityNameOrEmail(item),
    },
  })
}

const buildOptimisticEdge = (user: LegacyEntity) => {
  if (getInviteType(user) === InviteType.Email) {
    return {
      __typename: 'ViewerEdge' as const,
      cursor: '',
      node: { __typename: 'EmailInvite' as const, email: user.id },
    }
  }

  const isUser = user.__typename === 'BasicUser'
  return {
    __typename: 'ViewerEdge' as const,
    cursor: '',
    node: {
      __typename: 'ViewerEntity' as const,
      role: FileRole.Invited,
      permissions: [] as FilePermission[],
      entity: isUser
        ? {
            __typename: 'User' as const,
            id: user.id,
            name: user.name,
            username: (user as LegacyBasicUser).username,
            avatar: {
              __typename: 'Image' as const,
              url: (user as LegacyBasicUser).avatarUrl,
            },
          }
        : {
            __typename: 'Organization' as const,
            id: user.id,
            name: user.name,
            avatar: {
              __typename: 'Image' as const,
              url: (user as LegacyOrganization).avatarUrl,
            },
            owner: (user as LegacyOrganization).owner
              ? {
                  __typename: 'User' as const,
                  id: (user as LegacyOrganization).owner!.id,
                  name: (user as LegacyOrganization).owner!.name,
                }
              : { __typename: 'Organization' as const },
          },
    },
  }
}

const handleInviteClick = async () => {
  const invitedUsers = [...usersToInvite.value]

  const invites = invitedUsers.map((user) => ({
    ...(getInviteType(user) === InviteType.Email
      ? { email: user.id }
      : { id: user.id }),
    type: getInviteType(user),
  }))

  const currentEdges = result.value?.file?.viewers.edges ?? []
  const currentCount = result.value?.file?.viewers.totalCount ?? 0

  try {
    await sendInvites(
      { input: { fileId: props.file.id, invites } },
      {
        update(cache) {
          cache.modify({
            id: cache.identify({ __typename: 'File', id: props.file.id }),
            fields: {
              viewers(existing) {
                const data = existing as ViewersConnection
                if (!data?.edges) return existing

                return {
                  ...existing,
                  totalCount: currentCount + invitedUsers.length,
                  edges: [
                    ...currentEdges,
                    ...invitedUsers.map(buildOptimisticEdge),
                  ],
                }
              },
            },
          })
        },
      }
    )

    usersToInvite.value = []

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Invites were successfully sent',
    })
  } catch (error) {
    console.error(error)

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Something went wrong - please try again later',
    })
  }
}

defineExpose({ handleInviteClick, canSend, sendInvitesLoading })
</script>
